// ABOUTME: Retourkosten tab — manages the relation's ordered return-cost tariffs (objectBody array).
// ABOUTME: Geen-match fallback card on top, a dnd-sortable tariff list (order = priority), add modal + edit sidepanel.

import { Box, ButtonBase, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import RemoveIcon from "@mui/icons-material/Remove";

import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    TouchSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { tokens } from "../../../../../theme";
import { countriesForDropdown } from "../../../../../utils/countries";
import { showToastMessage, showToastMessageRequestError, showToastMessageUpdateRequest } from "../../../../../utils/toasts";
import {
    useGetSettingsForRelationQuery,
    useUpdateSettingForRelationMutation,
} from "../../../../../newapi/global/generalApiSlice";
import { useGetAllReturnShipperProfilesSelectQuery } from "../../../../../newapi/shipper/shipperprofileSlice";
import { useGetWebshopCredentialsSelectQuery } from "../../../../../newapi/webshop/webshopSlice";

import Heading from "../../../../../components/theme/text/Heading";
import Subheading from "../../../../../components/theme/text/Subheading";
import Text from "../../../../../components/theme/text/Text";
import Shbutton from "../../../../../components/theme/buttons/Shbutton";
import NumberSetting from "../../../../../components/global/Settings/NumberSetting";
import SidepanelLoader from "../../../../../components/theme/sidepanel2/SidepanelLoader";
import MAddReturnCostProfile from "../../../../../components/global/Modals/MAddReturnCostProfile";
import MConfirmDelete from "../../../../../components/global/Modals/MConfirmDelete";
import ShipperProfileLabel from "../../../../../components/global/ShipperProfileLabel";
import EMReturnCosts from "../../../../../components/global/ModalsPopover/explainers/returns/EMReturnCosts";
import ShGridEmpty from "../../../../../components/theme/datagrid2/Status/ShGridEmpty";
import SHQuickAction from "../../../../../components/theme/Modal/SHQuickAction";
import { generateColorFromTitle, generateIconColorFromTitle } from "../../../../../components/theme/TabView3/IconColorGenerator";

const SpReturnCostDetails = SidepanelLoader(() =>
    import("../../../../../components/global/Sidepanels/SpReturnCostDetails/SpReturnCostDetails")
);

const TARIFFS_KEY = "return_portal_tariffs";

const generateId = () =>
    (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `tariff_${Date.now()}_${Math.round(Math.random() * 1e6)}`;

const formatEuros = (cents) => `€ ${((parseInt(cents, 10) || 0) / 100).toFixed(2).replace(".", ",")}`;

const MAX_WEBSHOP_CHIPS = 3;
const MAX_COUNTRY_CHIPS = 3;

// Chip auto-colored from its label (same label -> same colour), like the TabView3 tabs.
const Chip = ({ label }) => {
    const theme = useTheme();
    const mode = theme.palette.mode;
    return (
        <Box
            sx={{
                px: 1.5,
                height: 32,
                display: "flex",
                alignItems: "center",
                borderRadius: 4,
                background: generateColorFromTitle(String(label), mode, mode === "dark" ? 55 : 90),
                color: generateIconColorFromTitle(String(label), mode),
                fontSize: 12,
                fontFamily: "Roboto, sans-serif",
                fontWeight: 600,
                whiteSpace: "nowrap",
            }}
        >
            {label}
        </Box>
    );
};

// Webshop chips: show the first few by name plus a "+N" count. Hovering anywhere
// over the whole chip group opens a popover listing every webshop.
const WebshopChips = ({ labels }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    if (labels.length <= MAX_WEBSHOP_CHIPS) {
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 0.5,
                    cursor: "default",
                    width:500,
                }}
            >
                {labels.map((label, i) => <Chip key={`w-${i}`} label={label} />)}
            </Box>
        )
    }

    const shown = labels.slice(0, MAX_WEBSHOP_CHIPS);
    const rest = labels.slice(MAX_WEBSHOP_CHIPS);

    return (
            <SHQuickAction
                disableScrollLock={true}
                component={
                    <Box sx={{ px: 1.5, py: 1, display: "flex", flexWrap: "wrap", gap: 0.5, maxWidth: 260 }}>
                        {labels.map((label, i) => <Chip key={`r-${i}`} label={label} />)}
                    </Box>
                }
            >
                {({ anchorEl, onMouseEnter, onMouseLeave, onTouchStart }) => (
                    <Box
                        aria-owns={anchorEl ? "simple-popover" : undefined}
                        aria-haspopup={false}
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                        onTouchStart={onTouchStart}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 0.5,
                            cursor: "default",
                            width:500,
                        }}
                    >
                        {shown.map((label, i) => <Chip key={`w-${i}`} label={label} />)}
                        <Box
                            sx={{
                                px: 1.5,
                                height: 32,
                                display: "flex",
                                alignItems: "center",
                                borderRadius: 4,
                                background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                color: colors.txt["secondary"],
                                fontSize: 12,
                                fontFamily: "Roboto, sans-serif",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                            }}
                        >
                            {`+${rest.length}`}
                        </Box>
                    </Box>
                )}
            </SHQuickAction>
    );
};

// Country chips: same treatment as the webshops — first few by name plus a "+N"
// count, with a hover popover over the whole group listing every country.
const CountryChips = ({ labels }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    if (labels.length <= MAX_COUNTRY_CHIPS) {
        
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 0.5,
                    cursor: "default",
                    width:500,
                }}
            >
                {labels.map((label, i) => <Chip key={`c-${i}`} label={label} />)}
            </Box>
        )
    }

    const shown = labels.slice(0, MAX_COUNTRY_CHIPS);
    const rest = labels.slice(MAX_COUNTRY_CHIPS);

    return (
            <SHQuickAction
                disableScrollLock={true}
                component={
                    <Box sx={{ px: 1.5, py: 1, display: "flex", flexWrap: "wrap", gap: 0.5, maxWidth: 260 }}>
                        {labels.map((label, i) => <Chip key={`r-${i}`} label={label} />)}
                    </Box>
                }
            >
                {({ anchorEl, onMouseEnter, onMouseLeave, onTouchStart }) => (
                    <Box
                        aria-owns={anchorEl ? "simple-popover" : undefined}
                        aria-haspopup={false}
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                        onTouchStart={onTouchStart}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 0.5,
                            cursor: "default",
                            width:500,
                        }}
                    >
                        {shown.map((label, i) => <Chip key={`c-${i}`} label={label} />)}
                        <Box
                            sx={{
                                px: 1.5,
                                height: 32,
                                display: "flex",
                                alignItems: "center",
                                borderRadius: 4,
                                background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                color: colors.txt["secondary"],
                                fontSize: 12,
                                fontFamily: "Roboto, sans-serif",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                            }}
                        >
                            {`+${rest.length}`}
                        </Box>
                    </Box>
                )}
            </SHQuickAction>
    );
};

const SortableTariffRow = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
        useSortable({ id: props.tariff.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        zIndex: isDragging ? 10 : "auto",
    };

    const [confirmOpen, setConfirmOpen] = useState(false);

    return (
        <Box
            ref={setNodeRef}
            style={style}
            {...attributes}
            sx={{
                display: "flex",
                alignItems: "center",
                px: 2,
                py: 1.5,
                borderRadius: 3,
                background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                boxShadow: theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
            }}
        >
            <Box
                ref={setActivatorNodeRef}
                {...listeners}
                sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    cursor: "grab", 
                    touchAction: "none", 
                    color: colors.txt["softened"],
                    mr:1,
                }}
            >
                <DragIndicatorIcon />
            </Box>

            <Box 
                sx={{ 
                    width: 24, 
                    textAlign: "center", 
                    flexShrink: 0,
                    background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                    borderRadius:4,
                    mr:2,
                }}
            >
                <Text bold>{props.index + 1}</Text>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems:"center", justifyContent:"space-between" }}>
                {props.profile ? 
                    <Box
                        sx={{
                            width:300,
                            flexShrink:0,
                        }}
                    >
                        <ShipperProfileLabel profile={props.profile} />
                    </Box>
                : <Text bold>{props.profileName}</Text>}
                <WebshopChips labels={props.webshopLabels} />
                <CountryChips labels={props.countryLabels} />
                <Box sx={{width:200}}/>
            </Box>

            <Box sx={{ flexShrink: 0, textAlign: "right", mr: 1 }}>
                <Subheading bold>{formatEuros(props.tariff.costInCents)}</Subheading>
            </Box>

            <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                <ButtonBase onClick={() => props.onEdit(props.tariff)} sx={{ p: 0.5, borderRadius: 2, color: colors.txt["secondary"] }}>
                    <EditOutlinedIcon fontSize="small" />
                </ButtonBase>
                <ButtonBase onClick={() => setConfirmOpen(true)} sx={{ p: 0.5, borderRadius: 2, color: colors.red[400] }}>
                    <DeleteOutlineIcon fontSize="small" />
                </ButtonBase>
            </Box>

            <MConfirmDelete
                open={confirmOpen}
                handleClose={() => setConfirmOpen(false)}
                data={props.profileName}
                confirm={() => props.onDelete(props.tariff.id)}
            />
        </Box>
    );
};

const ReturnCosts = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const environment = useSelector((state) => state.settings.environment);
    const language = useSelector((state) => state.settings.language);
    const relationId = environment.selectedId;

    const { data: settings } = useGetSettingsForRelationQuery(relationId, { skip: !relationId });
    const { data: returnProfiles } = useGetAllReturnShipperProfilesSelectQuery();
    const { data: webshops } = useGetWebshopCredentialsSelectQuery({ relationId: relationId, includeIntegrator: true }, { skip: !relationId });
    
    const countries = useMemo(() => countriesForDropdown(language), [language]);

    const [updateSettingForRelation] = useUpdateSettingForRelationMutation();

    const [tariffs, setTariffs] = useState([]);

    // Load the tariffs from the setting's objectBody; give each a stable id.
    useEffect(() => {
        if (!settings) {
            return;
        }
        const stored = settings.find((s) => s.key === TARIFFS_KEY)?.objectBody;
        const list = Array.isArray(stored) ? stored : [];
        setTariffs(list.map((tariff) => ({ ...tariff, id: tariff.id ?? generateId() })));
    }, [settings]);

    useEffect(() => {
        props.replaceView({ ...props.config });
    }, []);

    // Lookup maps for rendering the tariff rows.
    const profileNameById = useMemo(() => {
        const map = new Map();
        (returnProfiles ?? []).forEach((profile) => map.set(profile.id, profile.name));
        return map;
    }, [returnProfiles]);

    const profileById = useMemo(() => {
        const map = new Map();
        (returnProfiles ?? []).forEach((profile) => map.set(profile.id, profile));
        return map;
    }, [returnProfiles]);

    // -1 is the sentinel for manually created orders (they carry no webshop credentials).
    const webshopLabelById = useMemo(() => {
        const map = new Map([[-1, t("self_created")]]);
        (webshops ?? []).forEach((webshop) => map.set(webshop.id, webshop.description));
        return map;
    }, [webshops, t]);

    const countryNameByCode = useMemo(() => {
        const map = new Map();
        countries.forEach((country) => map.set(String(country.value).toLowerCase(), country.name));
        return map;
    }, [countries]);

    const webshopLabelsFor = (tariff) =>
        (tariff.webshopCredentials ?? []).map((id) => webshopLabelById.get(id) ?? `#${id}`);

    const countryLabelsFor = (tariff) =>
        (tariff.allowedCountries ?? []).map((code) => countryNameByCode.get(String(code).toLowerCase()) ?? String(code).toUpperCase());

    // Persist the whole tariff array into the relation setting's objectBody.
    const persist = async (nextTariffs) => {
        try {
            const resp = await updateSettingForRelation({ key: TARIFFS_KEY, objectBody: nextTariffs, value: "", relationId }).unwrap();
            showToastMessage("success",t("update_successfull"));
        } catch (err) {
            showToastMessageRequestError(t("update_failed"), t, err);
        }
    };

    const commit = (nextTariffs) => {
        setTariffs(nextTariffs);
        persist(nextTariffs);
    };

    const [addOpen, setAddOpen] = useState(false);
    const handleAdd = (newTariff) => {
        setAddOpen(false);
        commit([...tariffs, { ...newTariff, id: generateId() }]);
    };

    const [isExtended, setIsExtended] = useState(false);
    const [spLoading, setSpLoading] = useState();
    const [editingTariff, setEditingTariff] = useState(null);
    const handleEditOpen = (tariff) => {
        if (!spLoading) {
            setSpLoading(true);
        }
        setEditingTariff(tariff);
        setIsExtended(true);
    };
    const handleEditSave = (updatedTariff) => {
        commit(tariffs.map((tariff) => (tariff.id === updatedTariff.id ? { ...tariff, ...updatedTariff } : tariff)));
    };
    const handleDelete = (id) => {
        commit(tariffs.filter((tariff) => tariff.id !== id));
    };

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }
        const oldIndex = tariffs.findIndex((tariff) => tariff.id === active.id);
        const newIndex = tariffs.findIndex((tariff) => tariff.id === over.id);
        if (oldIndex < 0 || newIndex < 0) {
            return;
        }
        commit(arrayMove(tariffs, oldIndex, newIndex));
    };

    const fallbackRaw = settings?.find((s) => s.key === "return_portal_return_cost")?.value;
    const fallbackCents = parseInt(fallbackRaw, 10);

    return (
        <Box sx={{ height: 1, "@media screen and (max-width: 47.9375em)": { paddingTop: 0, paddingX: 0 } }}>
            <Box sx={{ display: "flex", flexDirection: "column", width: 1, paddingX: 4, paddingTop: 4, height: 1, gap: 3 }}>

                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Heading>{t("return_costs")}</Heading>
                            <EMReturnCosts />
                        </Box>
                        <Text light>{t("return_costs_subtitle")}</Text>
                    </Box>
                    <Shbutton
                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                        variant="contained"
                        endIcon={<AddIcon />}
                        onClick={() => setAddOpen(true)}
                    >
                        {t("new_return_cost_profile")}
                    </Shbutton>
                </Box>

                {/* Tariff list — priority-ordered tariffs, then the Geen match catch-all row. */}
                <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", display: "flex", flexDirection: "column", gap: 1.5, pb: 4 }}>
                    {tariffs.length === 0 ? (
                        <Box sx={{ py: 6, textAlign: "center" }}>
                            <ShGridEmpty/>
                        </Box>
                    ) : (
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={tariffs.map((tariff) => tariff.id)} strategy={verticalListSortingStrategy}>
                                {tariffs.map((tariff, index) => (
                                    <SortableTariffRow
                                        key={tariff.id}
                                        tariff={tariff}
                                        index={index}
                                        profile={profileById.get(tariff.shipperProfileId)}
                                        profileName={profileNameById.get(tariff.shipperProfileId) ?? `#${tariff.shipperProfileId}`}
                                        countryLabels={countryLabelsFor(tariff)}
                                        webshopLabels={webshopLabelsFor(tariff)}
                                        onEdit={handleEditOpen}
                                        onDelete={handleDelete}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>
                    )}

                    {/* Geen match — catch-all fallback, a flush row at the end of the list (hatched). */}
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                            px: 2,
                            py: 2,
                            borderRadius: 3,
                            border: `1px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200]}`,
                            background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Box 
                                sx={{ 
                                    width: 32, 
                                    height: 32, 
                                    borderRadius: "50%", 
                                    flexShrink: 0, 
                                    display: "flex", 
                                    alignItems: "center", 
                                    justifyContent: "center", 
                                    background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200], 
                                    color: colors.txt["primary"] 
                                }}
                            >
                                <RemoveIcon fontSize="small" />
                            </Box>
                             <NumberSetting
                                relationId={relationId}
                                settings={settings}
                                skey="return_portal_return_cost"
                                required
                                manualSave
                                extra={
                                    <Box sx={{ px: 2, display: "flex", alignItems: "center" }}>
                                        <Text noBreak bold>{t("in_cent")}</Text>
                                    </Box>
                                }
                                sx={{ 
                                    background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[200],
                                    borderTopRightRadius:0,
                                    borderBottomRightRadius:0,
                                    minWidth:270 
                                }}
                                inputSX={{
                                    backgroundColor: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                                }}
                                btnSx ={{
                                    backgroundColor: theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[400]
                                }}
                            />
                            {/* <Box sx={{ minWidth: 0,display:'flex',flexDirection:"column" }}>
                                <Text bold>{t("return_costs_nomatch_title")}</Text>
                                <Text light>{t("return_costs_nomatch_subtitle")}</Text>
                            </Box>
                            <Box sx={{ flex:1,background:"#fff" }}>
                                <NumberSetting
                                    relationId={relationId}
                                    title={false}
                                    subtitle={false}
                                    settings={settings}
                                    skey="return_portal_return_cost"
                                    required
                                    manualSave
                                    extra={
                                        <Box sx={{ px: 2, display: "flex", alignItems: "center" }}>
                                            <Text noBreak bold>{t("in_cent")}</Text>
                                        </Box>
                                    }
                                    sx={{ background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0] }}
                                />
                            </Box> */}
                        </Box>
                        {!isNaN(fallbackCents) && fallbackCents < 100 && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, height: 40, borderRadius: 5, background: colors.orange[100], border: `1px solid ${colors.orange[400]}`, color: colors.orange[400] }}>
                                <WarningAmberIcon />
                                <Text bold>{t("return_cost_below_100_warning")}</Text>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>

            <MAddReturnCostProfile
                open={addOpen}
                relationId={relationId}
                returnProfiles={returnProfiles ?? []}
                webshops={webshops ?? []}
                countries={countries}
                onAdd={handleAdd}
                handleClose={() => setAddOpen(false)}
            />

            {spLoading && (
                <SpReturnCostDetails
                    isExtended={isExtended}
                    setIsExtended={setIsExtended}
                    tariff={editingTariff}
                    returnProfiles={returnProfiles ?? []}
                    webshops={webshops ?? []}
                    countries={countries}
                    onSave={handleEditSave}
                    onDelete={handleDelete}
                />
            )}
        </Box>
    );
};

export default ReturnCosts;
